import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowDown, ArrowUp, Camera, Plus, Trash2, X } from 'lucide-react';
import { CHART_ORDER, CHART_PICTURES } from './ChartView';
import { cellText, defaultAgg, resolveDataset } from './engine';
import type {
  Aggregation, ChartVisual, DataVisualT, Field, Filter, Measure, PivotVisual, ReportDoc, TableVisual, Visual,
} from './model';

const AGGS: Aggregation[] = ['sum', 'avg', 'min', 'max', 'count', 'distinct', 'first'];
const field = 'px-2 py-1 !text-[11px] rounded border border-gn-input bg-gn-input text-gn-primary focus:outline-none focus:border-gn-blue';
const box = `w-full ${field}`;
const sectionTitle = 'text-[10px] font-semibold uppercase tracking-wider text-gn-secondary';

function FormatMark({ f }: { f?: Field }) {
  const s = f?.format === 'percent' ? '%' : f?.format === 'text' ? 'Aa' : '#';
  return <span className="inline-block w-5 text-center text-[9px] font-mono text-gn-muted">{s}</span>;
}

interface Props {
  doc: ReportDoc;
  visual: Visual;
  originLabels: Record<string, string>;
  onChange: (v: Visual) => void;
  onRetakeMap: () => void;
  retaking: boolean;
}

/** The right-hand pane: what the selected visual shows and how — chart by picture, fields into wells. */
export const Builder: React.FC<Props> = ({ doc, visual, originLabels, onChange, onRetakeMap, retaking }) => {
  const { t } = useTranslation();
  const fields = useMemo(
    () => ('datasetId' in visual ? resolveDataset(doc, visual.datasetId, originLabels).fields : []),
    [doc, visual, originLabels],
  );

  return (
    <div className="p-3 space-y-4 text-gn-primary">
      <div className="space-y-1">
        <div className={sectionTitle}>{t('studio.title')}</div>
        <input className={box} value={visual.title} onChange={(e) => onChange({ ...visual, title: e.target.value })} />
      </div>

      {'datasetId' in visual && (
        <div className="space-y-1">
          <div className={sectionTitle}>{t('studio.dataset')}</div>
          <select className={box} value={visual.datasetId}
                  onChange={(e) => onChange({ ...visual, datasetId: e.target.value, filters: [] } as Visual)}>
            {doc.datasets.map((d) => <option key={d.id} value={d.id}>{d.title}</option>)}
          </select>
        </div>
      )}

      {visual.kind === 'chart' && <ChartWells v={visual} fields={fields} onChange={onChange} />}
      {visual.kind === 'pivot' && <PivotWells v={visual} fields={fields} onChange={onChange} />}
      {visual.kind === 'table' && <TableWells v={visual} fields={fields} onChange={onChange} />}

      {'datasetId' in visual && (
        <Filters v={visual} fields={fields} doc={doc} originLabels={originLabels} onChange={(f, ex) => onChange({ ...visual, filters: f, excludeTotals: ex } as Visual)} />
      )}

      {visual.kind === 'map' && (
        <div className="space-y-2">
          <p className="text-[11px] text-gn-secondary">{doc.map ? t('studio.map_taken', { date: new Date(doc.map.takenAt).toLocaleString() }) : t('studio.map_none')}</p>
          {doc.map && doc.map.layers.length > 0 && (
            <ul className="text-[11px] list-disc pl-4 text-gn-secondary">{doc.map.layers.map((l) => <li key={l}>{l}</li>)}</ul>
          )}
          <button type="button" onClick={onRetakeMap} disabled={retaking}
                  className="flex items-center gap-1.5 px-2 py-1 text-[11px] rounded border border-gn hover:bg-gn-tertiary disabled:opacity-50">
            <Camera size={12} /> {retaking ? '…' : t('studio.map_retake')}
          </button>
          <p className="text-[10px] text-gn-muted">{t('studio.map_retake_hint')}</p>
        </div>
      )}
    </div>
  );
};

/* ───────────── chart ───────────── */

const ChartWells: React.FC<{ v: ChartVisual; fields: Field[]; onChange: (v: Visual) => void }> = ({ v, fields, onChange }) => {
  const { t } = useTranslation();
  const set = (patch: Partial<ChartVisual>) => onChange({ ...v, ...patch });
  const numeric = fields.filter((f) => f.format !== 'text');
  const scatter = v.chartType === 'scatter';
  const kpi = v.chartType === 'kpi';
  const single = v.chartType === 'pie' || v.chartType === 'donut' || kpi;

  return (
    <>
      <div className="space-y-1">
        <div className={sectionTitle}>{t('studio.chart_type')}</div>
        <div className="grid grid-cols-4 gap-1">
          {CHART_ORDER.map((ct) => (
            <button key={ct} type="button" title={t(`studio.type_${ct}`)} onClick={() => set({ chartType: ct })}
                    className={`flex flex-col items-center !p-1 rounded border ${v.chartType === ct ? 'border-gn-blue bg-gn-blue-light' : 'border-gn-light hover:border-gn'}`}>
              <div className="h-7 w-full [&>svg]:h-full [&>svg]:w-full">{CHART_PICTURES[ct]}</div>
              <div className="mt-0.5 text-[9px] leading-tight text-gn-secondary truncate">{t(`studio.type_${ct}`)}</div>
            </button>
          ))}
        </div>
      </div>

      {!scatter && !kpi && (
        <div className="space-y-1">
          <div className={sectionTitle}>{t('studio.axis_from')}</div>
          <div className="flex rounded border border-gn overflow-hidden text-[11px]">
            {(['rows', 'columns'] as const).map((o) => (
              <button key={o} type="button" onClick={() => set({ orientation: o, series: o === 'columns' ? '' : v.series })}
                      className={`flex-1 px-2 py-1 !text-[11px] !rounded-none ${v.orientation === o ? 'bg-gn-blue text-gn-inverse' : 'hover:bg-gn-tertiary'}`}>
                {t(`studio.axis_${o}`)}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-gn-muted">{t(v.orientation === 'rows' ? 'studio.axis_rows_hint' : 'studio.axis_columns_hint')}</p>
        </div>
      )}

      {!kpi && (
        <Well title={scatter ? t('studio.well_points') : v.orientation === 'columns' ? t('studio.well_series_rows') : t('studio.well_axis')}>
          <FieldSelect fields={fields} value={v.category} allowNone onChange={(k) => set({ category: k })} />
        </Well>
      )}

      {!scatter && !single && v.orientation === 'rows' && (
        <Well title={t('studio.well_legend')} hint={t('studio.well_legend_hint')}>
          <FieldSelect fields={fields.filter((f) => f.key !== v.category)} value={v.series} allowNone onChange={(k) => set({ series: k })} />
        </Well>
      )}

      <Well title={scatter ? t('studio.well_xy') : kpi ? t('studio.well_value') : t('studio.well_values')}>
        <Measures
          measures={v.measures}
          fields={fields}
          max={kpi || (single && v.orientation === 'rows') || (!!v.series && v.orientation === 'rows') ? 1 : undefined}
          onChange={(m) => set({ measures: m })}
        />
        {!kpi && numeric.length > v.measures.length && !(single && v.orientation === 'rows') && !v.series && (
          <button type="button" className="text-[10px] text-gn-blue hover:underline"
                  onClick={() => set({ measures: numeric.map((f) => v.measures.find((m) => m.field === f.key) ?? { field: f.key, agg: defaultAgg(f) }) })}>
            {t('studio.all_numeric')}
          </button>
        )}
      </Well>

      {!kpi && !scatter && (
        <div className="grid grid-cols-2 gap-2">
          <label className="space-y-1">
            <span className={sectionTitle}>{t('studio.sort')}</span>
            <select className={box} value={v.sort} onChange={(e) => set({ sort: e.target.value as ChartVisual['sort'] })}>
              <option value="none">{t('studio.sort_none')}</option>
              <option value="desc">{t('studio.sort_desc')}</option>
              <option value="asc">{t('studio.sort_asc')}</option>
            </select>
          </label>
          <label className="space-y-1">
            <span className={sectionTitle}>{t('studio.top_n')}</span>
            <input type="number" min={0} className={box} value={v.topN || ''} placeholder={t('studio.all')}
                   onChange={(e) => set({ topN: Math.max(0, Number(e.target.value) || 0) })} />
          </label>
        </div>
      )}
      {!kpi && (
        <label className="flex items-center gap-2 text-[11px]">
          <input type="checkbox" checked={v.showLabels} onChange={(e) => set({ showLabels: e.target.checked })} />
          {t('studio.show_labels')}
        </label>
      )}
    </>
  );
};

/* ───────────── cross table ───────────── */

const PivotWells: React.FC<{ v: PivotVisual; fields: Field[]; onChange: (v: Visual) => void }> = ({ v, fields, onChange }) => {
  const { t } = useTranslation();
  const set = (patch: Partial<PivotVisual>) => onChange({ ...v, ...patch });
  return (
    <>
      <Well title={t('studio.well_rows')}>
        <FieldList keys={v.rows} fields={fields} onChange={(rows) => set({ rows })} />
      </Well>
      <Well title={t('studio.well_columns')}>
        <FieldList keys={v.columns} fields={fields} onChange={(columns) => set({ columns })} />
      </Well>
      <Well title={t('studio.well_value')}>
        <Measures measures={[v.measure]} fields={fields} max={1} onChange={(m) => m[0] && set({ measure: m[0] })} />
      </Well>
      <label className="flex items-center gap-2 text-[11px]">
        <input type="checkbox" checked={v.totals} onChange={(e) => set({ totals: e.target.checked })} />
        {t('studio.totals')}
      </label>
    </>
  );
};

/* ───────────── table ───────────── */

const TableWells: React.FC<{ v: TableVisual; fields: Field[]; onChange: (v: Visual) => void }> = ({ v, fields, onChange }) => {
  const { t } = useTranslation();
  const shown = v.columns.length ? v.columns : fields.map((f) => f.key);
  const set = (columns: string[]) => onChange({ ...v, columns: columns.length === fields.length && columns.every((c, i) => c === fields[i].key) ? [] : columns });
  const move = (i: number, d: number) => {
    const next = [...shown];
    const j = i + d;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    set(next);
  };
  return (
    <Well title={t('studio.well_table_columns')}>
      <div className="max-h-64 overflow-auto space-y-0.5">
        {shown.map((k, i) => {
          const f = fields.find((x) => x.key === k);
          return (
            <div key={k} className="flex items-center gap-1 text-[11px]">
              <input type="checkbox" checked onChange={() => set(shown.filter((x) => x !== k))} />
              <FormatMark f={f} />
              <span className="flex-1 truncate">{f?.label ?? k}</span>
              <button type="button" className="p-0.5 hover:text-gn-blue" onClick={() => move(i, -1)}><ArrowUp size={11} /></button>
              <button type="button" className="p-0.5 hover:text-gn-blue" onClick={() => move(i, 1)}><ArrowDown size={11} /></button>
            </div>
          );
        })}
        {fields.filter((f) => !shown.includes(f.key)).map((f) => (
          <div key={f.key} className="flex items-center gap-1 text-[11px] text-gn-muted">
            <input type="checkbox" checked={false} onChange={() => set([...shown, f.key])} />
            <FormatMark f={f} />
            <span className="flex-1 truncate">{f.label}</span>
          </div>
        ))}
      </div>
    </Well>
  );
};

/* ───────────── shared pieces ───────────── */

const Well: React.FC<{ title: string; hint?: string; children: React.ReactNode }> = ({ title, hint, children }) => (
  <div className="space-y-1 rounded border border-dashed border-gn p-2">
    <div className={sectionTitle}>{title}</div>
    {children}
    {hint && <p className="text-[10px] text-gn-muted">{hint}</p>}
  </div>
);

const FieldSelect: React.FC<{ fields: Field[]; value: string; onChange: (k: string) => void; allowNone?: boolean }> = ({ fields, value, onChange, allowNone }) => {
  const { t } = useTranslation();
  return (
    <select className={box} value={value} onChange={(e) => onChange(e.target.value)}>
      {allowNone && <option value="">{t('studio.none')}</option>}
      {fields.map((f) => (
        <option key={f.key} value={f.key}>{f.format === 'text' ? 'Aa ' : f.format === 'percent' ? '% ' : '# '}{f.label}</option>
      ))}
    </select>
  );
};

const FieldList: React.FC<{ keys: string[]; fields: Field[]; onChange: (k: string[]) => void }> = ({ keys, fields, onChange }) => {
  const { t } = useTranslation();
  const [adding, setAdding] = useState('');
  return (
    <div className="space-y-1">
      {keys.map((k) => (
        <div key={k} className="flex items-center gap-1 text-[11px] px-1.5 py-0.5 rounded bg-gn-tertiary">
          <FormatMark f={fields.find((f) => f.key === k)} />
          <span className="flex-1 truncate">{fields.find((f) => f.key === k)?.label ?? k}</span>
          <button type="button" className="hover:text-gn-danger" onClick={() => onChange(keys.filter((x) => x !== k))}><X size={11} /></button>
        </div>
      ))}
      <select className={box} value={adding} onChange={(e) => { if (e.target.value) onChange([...keys, e.target.value]); setAdding(''); }}>
        <option value="">{t('studio.add_field')}</option>
        {fields.filter((f) => !keys.includes(f.key)).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
      </select>
    </div>
  );
};

const Measures: React.FC<{ measures: Measure[]; fields: Field[]; max?: number; onChange: (m: Measure[]) => void }> = ({ measures, fields, max, onChange }) => {
  const { t } = useTranslation();
  const list = max ? measures.slice(0, max) : measures;
  return (
    <div className="space-y-1">
      {list.map((m, i) => (
        <div key={i} className="flex items-center gap-1">
          <select className={`${field} flex-1 min-w-0`} value={m.field}
                  onChange={(e) => {
                    const f = fields.find((x) => x.key === e.target.value);
                    onChange(list.map((x, j) => (j === i ? { field: e.target.value, agg: defaultAgg(f) } : x)));
                  }}>
            {fields.map((f) => <option key={f.key} value={f.key}>{f.format === 'text' ? 'Aa ' : f.format === 'percent' ? '% ' : '# '}{f.label}</option>)}
          </select>
          <select className={`${field} w-24 shrink-0`} value={m.agg} onChange={(e) => onChange(list.map((x, j) => (j === i ? { ...x, agg: e.target.value as Aggregation } : x)))}>
            {AGGS.map((a) => <option key={a} value={a}>{t(`studio.agg_${a}`)}</option>)}
          </select>
          {(!max || max > 1) && (
            <button type="button" className="p-0.5 hover:text-gn-danger" onClick={() => onChange(list.filter((_, j) => j !== i))}><Trash2 size={12} /></button>
          )}
        </div>
      ))}
      {(!max || list.length < max) && fields.length > 0 && (
        <button type="button" className="flex items-center gap-1 text-[10px] text-gn-blue hover:underline"
                onClick={() => {
                  const f = fields.find((x) => x.format !== 'text' && !list.some((m) => m.field === x.key)) ?? fields[0];
                  onChange([...list, { field: f.key, agg: defaultAgg(f) }]);
                }}>
          <Plus size={11} /> {t('studio.add_value')}
        </button>
      )}
    </div>
  );
};

const Filters: React.FC<{
  v: DataVisualT;
  fields: Field[];
  doc: ReportDoc;
  originLabels: Record<string, string>;
  onChange: (f: Filter[], excludeTotals: boolean) => void;
}> = ({ v, fields, doc, originLabels, onChange }) => {
  const { t } = useTranslation();
  const rows = useMemo(() => resolveDataset(doc, v.datasetId, originLabels).rows, [doc, v.datasetId, originLabels]);
  const valuesOf = (k: string) => [...new Set(rows.map((r) => cellText(r[k] ?? null)))].slice(0, 300);
  const [adding, setAdding] = useState('');
  return (
    <div className="space-y-1 rounded border border-dashed border-gn p-2">
      <div className={sectionTitle}>{t('studio.filters')}</div>
      <label className="flex items-center gap-2 text-[11px]">
        <input type="checkbox" checked={v.excludeTotals} onChange={(e) => onChange(v.filters, e.target.checked)} />
        {t('studio.exclude_totals')}
      </label>
      {v.filters.map((f, i) => {
        const all = valuesOf(f.field);
        return (
          <div key={i} className="rounded bg-gn-tertiary p-1.5 space-y-1">
            <div className="flex items-center gap-1 text-[11px]">
              <span className="flex-1 truncate font-medium">{fields.find((x) => x.key === f.field)?.label ?? f.field}</span>
              <button type="button" className="hover:text-gn-danger" onClick={() => onChange(v.filters.filter((_, j) => j !== i), v.excludeTotals)}><X size={11} /></button>
            </div>
            <div className="max-h-32 overflow-auto space-y-0.5">
              {all.map((val) => (
                <label key={val} className="flex items-center gap-1.5 text-[11px]">
                  <input type="checkbox" checked={f.values.length === 0 || f.values.includes(val)}
                         onChange={(e) => {
                           const cur = f.values.length === 0 ? all : f.values;
                           const next = e.target.checked ? [...cur, val] : cur.filter((x) => x !== val);
                           onChange(v.filters.map((x, j) => (j === i ? { ...x, values: next.length === all.length ? [] : next } : x)), v.excludeTotals);
                         }} />
                  <span className="truncate">{val || '—'}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}
      <select className={box} value={adding} onChange={(e) => {
        if (e.target.value) onChange([...v.filters, { field: e.target.value, values: [] }], v.excludeTotals);
        setAdding('');
      }}>
        <option value="">{t('studio.add_filter')}</option>
        {fields.filter((f) => !v.filters.some((x) => x.field === f.key)).map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
      </select>
    </div>
  );
};
